import { Controller } from "@hotwired/stimulus";

const SIDEBAR_COLLAPSED_KEY = "sidebar_collapsed";
const APP_SESSION_ID_KEY = "app_session_id";
const CTA_SESSION_DISMISSED_PREFIX = "cta_";
const CTA_SESSION_DISMISSED_SUFFIX = "_session_dismissed";

export default class extends Controller {
  static targets = ["sidebar", "submenu", "keyword", "cta", "ctatitle"];

  connect() {
    // Clear session-dismiss flags when login session changes
    const currentSessionId = document.body?.getAttribute?.("data-app-session-id");
    if (currentSessionId) {
      const storedSessionId = sessionStorage.getItem(APP_SESSION_ID_KEY);
      if (storedSessionId && storedSessionId !== currentSessionId) {
        this.clearSessionDismissKeys();
      }
      sessionStorage.setItem(APP_SESSION_ID_KEY, currentSessionId);
    }

    if (localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true") {
      this.sidebarTarget.classList.add("collapsed");
      this.submenuTargets.forEach(el => el.classList.add("hidden"));
      const next = this.element.nextElementSibling;
      if (!next) return;
      next.classList.remove("ml-64");
      next.classList.add("ml-16");
    }

    // CTA visibility:
    // "Don't Show Again": hides for a user until browser cache is cleared (localStorage)
    // "Dismiss": hides for a user until their session expires/ends (sessionStorage)
    if (this.hasCtaTarget && this.ctaTarget.dataset.ctaId) {
      const ctaId = this.ctaTarget.dataset.ctaId;
      const permanentKey = `cta_${ctaId}_dismissed`;
      const sessionKey = `cta_${ctaId}_session_dismissed`;
      const isPermanentDismissed = localStorage.getItem(permanentKey) === 'true';
      const isSessionDismissed = sessionStorage.getItem(sessionKey) === 'true';
      if (isPermanentDismissed || isSessionDismissed) {
        this.ctaTarget.classList.add("hidden");
      } else {
        // Migrate old cookie to localStorage for permanent dismissals
        const cookieMatch = document.cookie.match(new RegExp('(?:^|; )' + permanentKey.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
        if (cookieMatch && cookieMatch[1] === 'true') {
          localStorage.setItem(permanentKey, 'true');
          this.ctaTarget.classList.add("hidden");
        }
      }
    }

    this.expand_active_submenus();
  }

  collapse_click() {
    const wasCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true"

    if (wasCollapsed) {
      this.expand_sidebar();
    } else {
      this.collapse_sidebar();
    }
  }

  expand_sidebar() {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, "false");
    this.sidebarTarget.classList.remove("collapsed");
    this.submenuTargets.forEach(el => el.classList.add("hidden"))

    const next = this.element.nextElementSibling;
    if (!next) return;

    next.classList.remove("ml-16");
    next.classList.add("ml-64");
  }

  collapse_sidebar() {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, "true");
    this.sidebarTarget.classList.add("collapsed");
    this.submenuTargets.forEach(el => el.classList.add("hidden"))

    const next = this.element.nextElementSibling;
    if (!next) return;

    next.classList.remove("ml-64");
    next.classList.add("ml-16");
  }

  search_click() {
    const wasCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true"
    if (wasCollapsed) {
      this.expand_sidebar();
    }
    this.keywordTarget.focus();
  }

  search() {
    const keyword = this.keywordTarget.value.toLowerCase();
  }

  submenu_click(event) {
    event.preventDefault();
    const submenuId = event.currentTarget.dataset.submenuId;
    this.toggle_submenu(submenuId, event.currentTarget);
  }

  toggle_submenu(submenuId, buttonElement) {
    const submenu = document.getElementById(submenuId);
    const arrowDown = buttonElement.querySelector('[data-navigation-target="arrow-down"]');
    const arrowUp = buttonElement.querySelector('[data-navigation-target="arrow-up"]');

    if (!this.sidebarTarget.classList.contains("collapsed")) {
      if (submenu && submenu.dataset.navigationTarget === "submenu") {
        submenu.classList.toggle("hidden");
        if (arrowDown && arrowUp) {
          arrowDown.classList.toggle("hidden");
          arrowUp.classList.toggle("hidden");
        }
      }
    }
  }

  expand_active_submenus() {
    this.submenuTargets.forEach(submenu => {
      const activeItems = submenu.querySelectorAll('a span.text-primary-400');
      if (activeItems.length > 0) {
        const submenuId = submenu.id;
        const buttonElement = document.querySelector(`[data-submenu-id="${submenuId}"]`);
        if (buttonElement) {
          this.toggle_submenu(submenuId, buttonElement);
        }
      }
    });
  }

  clearSessionDismissKeys() {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CTA_SESSION_DISMISSED_PREFIX) && key?.endsWith(CTA_SESSION_DISMISSED_SUFFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  }

  // "Dismiss" - Session dismiss: hidden until next login/session (sessionStorage, cleared on new login)
  session_dismiss_click(event) {
    if (event) event.stopPropagation();
    this.ctaTarget.classList.add("hidden");
    const ctaId = this.ctaTarget.dataset.ctaId;
    if (ctaId) {
      sessionStorage.setItem(`cta_${ctaId}_session_dismissed`, 'true');
    }
  }

  // "Don't Show Again" - Permanent dismiss: hidden until user clears browser data (localStorage)
  permanent_dismiss_click(event) {
    if (event) event.stopPropagation();
    this.ctaTarget.classList.add("hidden");
    const ctaId = this.ctaTarget.dataset.ctaId;
    if (ctaId) {
      localStorage.setItem(`cta_${ctaId}_dismissed`, 'true');
    } else {
      const ctaTitle = this.ctatitleTarget?.textContent
        ?.toLowerCase()
        ?.replace(/[^a-z0-9]+/g, '_')
        ?.replace(/^_+|_+$/g, '') ?? 'cta';
      document.cookie = `cta_${ctaTitle}=true; path=/;`;
    }
  }

  cta_click() {
    const wasCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true"
    if (wasCollapsed) {
      this.expand_sidebar();
    }
  }
}
